import {useCallback,useEffect,useState} from 'react';
import {Link} from 'react-router-dom';
import {ArrowLeft,RefreshCw,ShieldCheck,AlertTriangle,ExternalLink} from 'lucide-react';
import {Card,PageHeader} from '../components/UI';
import {api,money} from '../lib/api';

const badge=(value,yes='Ready',no='Needs attention')=><strong className={value?'studio-readiness-yes':'studio-readiness-no'}>{value?yes:no}</strong>;
const connectionBadge=value=>value==null?<strong className="studio-readiness-muted">Not checked</strong>:badge(value,'Connected','Not connected');
function explainConnection(reason){
 if(reason==='STUDIO_OWNERSHIP_SHARED_SECRET_MISSING')return 'CRM is missing the STUDIO_SITE_OWNERSHIP_TOKEN secret. Configure the same secure token (at least 32 characters) on the CRM Worker and Profiles Pages in Cloudflare. This is a technical problem, not a Board approval decision.';
 if(reason==='PROFILES_HANDSHAKE_UNREACHABLE')return 'The CRM Worker could not connect to profiles.sousamurray.co.uk. Check that the custom domain resolves and the Pages API is reachable; this is no longer a missing-secret error.';
 if(reason==='PROFILES_HANDSHAKE_TIMED_OUT')return 'Profiles did not respond before the network timeout. Confirm the Pages API is reachable and the Profiles-to-CRM service binding responds.';
 if(reason==='PROFILES_HANDSHAKE_NOT_JSON')return 'Profiles returned a web page instead of the API response. Check that the Cloudflare Pages Functions endpoint is deployed and not caught by a website router.';
 if(String(reason||'').startsWith('PROFILES_HANDSHAKE_REDIRECT_'))return 'Cloudflare redirected the Profiles API request instead of serving it directly. Check hostname and redirect rules; the private token was not forwarded.';

 if(reason==='CROSS_SERVICE_BINDING_NOT_VERIFIED')return 'The services responded, but the database, private CRM binding or return connection has not been verified.';
 if(String(reason||'').startsWith('PROFILES_HANDSHAKE_HTTP_')){
  const status=String(reason).split('_').at(-1);
  if(status==='404')return 'HTTP 404: Profiles did not accept the CRM private connection request. This often means the shared token is absent or different in the active Profiles Production deployment. Confirm the domain belongs to the Pages project where you added the secret, then redeploy that Pages project.';
  if(status==='403'||status==='401')return 'HTTP '+status+': A Cloudflare access rule or another security control may be blocking the CRM-to-Profiles request. Check Access/WAF settings for this exact API route.';
  if(status==='405')return 'HTTP 405: The connection request is reaching a handler that does not permit POST. The CRM requires a POST request and the Profiles endpoint must support it.';
  return 'Profiles returned HTTP '+status+' during the private connection check. This is a server response, not a Stripe price error. Check the Cloudflare Pages Functions logs.';
 }
 return reason||'CRM and Profiles connection verified.';
}

/** Decision register, not the authority granting customer entitlements.
 * The Board must agree each proposed figure and separately authorise release.
 */
const boardDraftFields=[
 ['freeProfileLinks','Free Profile — maximum links',10000],
 ['plusProfileLinks','Plus Profile — maximum links',10000],
 ['professionalProfileLinks','Professional Profile — maximum links',10000],
 ['organisationProfileLinks','Organisation Profile — maximum links',10000],
 ['organisationTeamSeats','Organisation Profile — team seats',1000],
 ['essentialSections','Essential website — maximum sections',1000],
 ['localBusinessSections','Local Business website — maximum sections',1000],
 ['premiumSections','Premium website — maximum sections',1000],
 ['multiSiteSections','Multi-Site website — maximum sections per site',1000],
 ['essentialStorageMb','Essential website — storage (MB)',100000],
 ['localBusinessStorageMb','Local Business website — storage (MB)',100000],
 ['premiumStorageMb','Premium website — storage (MB)',100000],
 ['multiSiteStorageMb','Multi-Site — storage (MB)',100000],
 ['essentialMonthlyBandwidthMb','Essential — monthly bandwidth (MB)',1000000],
 ['localBusinessMonthlyBandwidthMb','Local Business — monthly bandwidth (MB)',1000000],
 ['premiumMonthlyBandwidthMb','Premium — monthly bandwidth (MB)',1000000],
 ['multiSiteMonthlyBandwidthMb','Multi-Site — monthly bandwidth (MB)',1000000],
 ['essentialCustomDomains','Essential — custom domains per site',100],
 ['localBusinessCustomDomains','Local Business — custom domains per site',100],
 ['premiumCustomDomains','Premium — custom domains per site',100],
 ['multiSiteCustomDomains','Multi-Site — custom domains per site',100],
 ['essentialTemplateCount','Essential — templates available (max 62)',62],
 ['localBusinessTemplateCount','Local Business — templates available (max 62)',62],
 ['premiumTemplateCount','Premium — templates available (max 62)',62],
 ['multiSiteTemplateCount','Multi-Site — templates available (max 62)',62],
 ['essentialAnalyticsHistoryDays','Essential — analytics history (days)',3650],
 ['localBusinessAnalyticsHistoryDays','Local Business — analytics history (days)',3650],
 ['premiumAnalyticsHistoryDays','Premium — analytics history (days)',3650],
 ['multiSiteAnalyticsHistoryDays','Multi-Site — analytics history (days)',3650],
 ['essentialSupportResponseHours','Essential — support response hours',720],
 ['localBusinessSupportResponseHours','Local Business — support response hours',720],
 ['premiumSupportResponseHours','Premium — support response hours',720],
 ['multiSiteSupportResponseHours','Multi-Site — support response hours',720],
 ['multiSiteTeamSeats','Multi-Site — team seats',1000],
 ['directorDiscountPercent','Director discount proposal (%)',100],
 ['employeeDiscountPercent','Employee discount proposal (%)',100],
 ['contractorDiscountPercent','Contractor discount proposal (%)',100],
];


/** Central operational oversight for BOTH subscription product families.
 * Reads only an authenticated CRM endpoint. The live Stripe secret never
 * reaches the browser; read-only verification never creates charges.
 */
export default function StudioControlCentre(){
 const [status,setStatus]=useState(null);
 const [connectionStatus,setConnectionStatus]=useState(null);
 const [connectionBusy,setConnectionBusy]=useState(false);
 const [connectionError,setConnectionError]=useState('');
 const checkConnections=async()=>{
  setConnectionBusy(true);setConnectionError('');
  try{
   const data=await api('/api/crm/studio/readiness?verify_connections=1');
   if(!data?.success||!data?.connections)
    throw new Error('Cloudflare did not return a service-connection report.');
   setConnectionStatus(data.connections);
  }catch(error){setConnectionError(error?.message||'Connection checks failed.');}
  finally{setConnectionBusy(false)}
 };

 const [error,setError]=useState('');
 const [busy,setBusy]=useState(false);
 const [checkedAt,setCheckedAt]=useState('');
 const [operations,setOperations]=useState(null);
 const [settingsDraft,setSettingsDraft]=useState(null);
 const [settingsReason,setSettingsReason]=useState('');
 const [settingsNotice,setSettingsNotice]=useState('');
 const [settingsBusy,setSettingsBusy]=useState(false);
 const [settingsError,setSettingsError]=useState('');
 const [approvalForm,setApprovalForm]=useState({reference:'',authority:'',decidedAt:'',reason:'',acknowledged:false});
 const [approvalBusy,setApprovalBusy]=useState(false);
 const [approvalError,setApprovalError]=useState('');
 const [approvalNotice,setApprovalNotice]=useState('');
 const approvalChange=(key,value)=>setApprovalForm(previous=>({...previous,[key]:value}));
 const [upgradeTarget,setUpgradeTarget]=useState({customerNumber:'',scope:'standalone',site_id:'',interval:'month'});
 const [upgradeQuote,setUpgradeQuote]=useState(null);
 const [upgradeQuoteError,setUpgradeQuoteError]=useState('');
 const [upgradeQuoteBusy,setUpgradeQuoteBusy]=useState(false);
 const [customerEntitlements,setCustomerEntitlements]=useState(null);
 const [entitlementsError,setEntitlementsError]=useState('');
 const [entitlementsBusy,setEntitlementsBusy]=useState(false);
 const inspectCustomer=async()=>{
  setEntitlementsBusy(true);setEntitlementsError('');setCustomerEntitlements(null);
  try{
   const result=await api('/api/crm/studio/customer-entitlements',{
    method:'POST',headers:{'content-type':'application/json'},
    body:JSON.stringify({customerNumber:upgradeTarget.customerNumber}),
   });
   if(!result?.success||result.customerNumber!==upgradeTarget.customerNumber)
    throw Error('CRM did not confirm the selected customer.');
   setCustomerEntitlements(result);
  }catch(error){setEntitlementsError(error?.message||'Could not inspect Studio subscriptions.');}
  finally{setEntitlementsBusy(false)}
 };

 const verifyUpgrade=async()=>{
  setUpgradeQuoteBusy(true);setUpgradeQuoteError('');setUpgradeQuote(null);
  try{
   const result=await api('/api/crm/studio/upgrade-quote',{
    method:'POST',headers:{'content-type':'application/json'},
    body:JSON.stringify({...upgradeTarget,site_id:Number(upgradeTarget.site_id)}),
   });
   setUpgradeQuote(result);
  }catch(error){setUpgradeQuoteError(error?.message||'Cannot verify this website upgrade.');}
  finally{setUpgradeQuoteBusy(false)}
 };

 const loadSettings=useCallback(async()=>{
  try{
   const result=await api('/api/crm/studio/settings');
   if(!result?.success)throw Error('Studio configuration response was invalid.');
   setOperations(result);setSettingsDraft(result.settings||null);setSettingsError('');
   setApprovalForm({reference:result.settings?.commercialApprovalReference||'',
    authority:result.settings?.commercialApprovalAuthority||'',
    decidedAt:result.settings?.commercialApprovalDate||'',reason:'',acknowledged:false});
  }catch(err){setSettingsError(err.message||'CRM Studio settings are unavailable.');}
 },[]);
 const saveSettings=async()=>{
  if(!settingsDraft||settingsBusy)return;
  setSettingsBusy(true);setSettingsNotice('');
  try{
   const result=await api('/api/crm/studio/settings',{
     method:'PATCH',headers:{'content-type':'application/json'},
     body:JSON.stringify({settings:settingsDraft,reason:settingsReason}),
   });
   if(!result?.success)throw Error('Settings were not saved.');
   setOperations(result);setSettingsDraft(result.settings);
   setSettingsReason('');
   setSettingsNotice('Studio operating settings saved with an audit record. No live charges or commercial flags were activated.');
   setSettingsError('');
  }catch(err){setSettingsError(err.message||'Unable to save Studio operational settings.');}
  finally{setSettingsBusy(false);}
 };
 const recordApproval=async event=>{
  event.preventDefault();
  if(approvalBusy||!approvalForm.acknowledged)return;
  setApprovalBusy(true);setApprovalError('');setApprovalNotice('');
  try{
   const saved=await api('/api/crm/studio/pricing-decision',{
    method:'POST',headers:{'content-type':'application/json'},
    body:JSON.stringify({
     reference:approvalForm.reference,authority:approvalForm.authority,
     decidedAt:approvalForm.decidedAt,reason:approvalForm.reason,
     pricingVersion:operations?.effective?.approvedPriceVersion||'',
     acknowledged:approvalForm.acknowledged,
    }),
   });
   if(!saved?.success||!saved?.decisionRecorded)throw Error('CRM did not confirm an audited pricing decision.');
   setOperations(saved);
   setSettingsDraft(previous=>previous?({...previous,
    commercialApprovalReference:saved.settings.commercialApprovalReference,
    commercialApprovalAuthority:saved.settings.commercialApprovalAuthority,
    commercialApprovalDate:saved.settings.commercialApprovalDate}):saved.settings);
   setApprovalForm(previous=>({...previous,reason:'',acknowledged:false}));
   setApprovalNotice('The Board pricing decision has been saved in the CRM audit ledger. No payment was created. If all other production switches are enabled, Stripe LIVE Checkout may now open.');
   void load(false);
  }catch(err){setApprovalError(err?.message||'CRM could not record the pricing decision.');}
  finally{setApprovalBusy(false);}
 };
 const change=(key,value)=>setSettingsDraft(old=>({...old,[key]:value}));
 const load=useCallback(async(verify=false)=>{
  setBusy(true);setError('');
  try{
   const data=await api('/api/crm/studio/readiness'+(verify?'?verify_stripe=1':''));
   if(!data?.success)throw Error('Studio readiness was not returned by CRM.');
   setStatus(old=>verify?data:{...old,...data,stripeCatalogue:old?.stripeCatalogue||null});
   if(verify)setCheckedAt(new Date().toISOString());
  }catch(e){setError(e?.message||'Unable to read Studio configuration.');}
  finally{setBusy(false);}
 },[]);
 useEffect(()=>{void load(false);void loadSettings();},[load,loadSettings]);
 const config=status?.configuration||{};
 const catalogue=status?.stripeCatalogue;
 const blockers=[
  !config.liveStripeConfigured&&'CRM does not report a Stripe LIVE key.',
  !config.commercialApproved&&'The commercial approval switch in Cloudflare is OFF. A Board decision does not automatically update this switch.',
  !config.commercialApprovalRecorded&&'The signed approval reference or matching pricing version has not been confirmed in CRM.',
  !config.provisioningReady&&'Subscription provisioning is OFF. Customer access after payment must be verified before activating this.',
 ].filter(Boolean);
 return <main className="studio-readiness-page">
  <PageHeader eyebrow="SM-CUSTOMER-OPERATIONS / STUDIO"
   title="Profiles & Mini Websites — Payments and Plans"
   description="Find what is blocking Stripe Checkout, inspect live connections and record plan settings. This page does not create charges."
   actions={<button className="button primary govuk-button" disabled={busy} onClick={()=>void load(true)}>
    <RefreshCw size={16}/>{busy?'Checking live Stripe…':'Verify all live Stripe prices'}</button>}/>
  <div className="studio-readiness-back"><Link to="/settings"><ArrowLeft size={15}/>Back to CRM settings</Link></div>
  {error&&<p role="alert" className="operation-notice error govuk-body">{error}</p>}
  {status&&<section className="studio-checkout-explainer" aria-label="Why checkout is not opening">
   <h2 className="govuk-heading-m">{config.liveCheckoutReady?'Checkout activation switches are configured':'Why Stripe Checkout is not opening'}</h2>
   <p className="govuk-body">{config.liveCheckoutReady?'CRM reports the main settings are configured. The real purchase and customer-access journey still needs verification.':'Your CRM is stopping checkout because one or more of these technical settings is incomplete:'}</p>
   {blockers.length>0&&<ol>{blockers.map(item=><li key={item}>{item}</li>)}</ol>}
   <p className="studio-checkout-note govuk-body">A Board approval decision and Cloudflare configuration are separate things. Saving the settings below does not automatically activate payments.</p>
  </section>}
  <Card title="1. Is LIVE checkout configured?">
   <div className="studio-readiness-grid">
    <p className="govuk-body">Stripe account mode {badge(config.liveStripeConfigured,"LIVE key detected","LIVE key missing")}<small>{config.stripeEnvironment||'Checking'} — live credentials must be in CRM Worker secrets.</small></p>
    <p className="govuk-body">Commercial approval {badge(config.commercialApproved,"Enabled in Cloudflare","OFF in Cloudflare")}<small>Separate Board-approved pricing version. No customer charges are authorised by this page.</small></p>
    <p className="govuk-body">Subscription provisioning {badge(config.provisioningReady,"Enabled","Not enabled")}<small>Activation must be linked to verified Stripe subscriptions.</small></p>
    <p className="govuk-body">Approved Multi-Site capacity <strong>{config.multiSiteLimit||'Not approved'}</strong><small>Only exact positive website counts can be provisioned.</small></p>
    <p className="govuk-body">Live checkout readiness {badge(config.liveCheckoutReady,"Configured","Blocked")}<small>Requires ALL necessary safeguards; readiness is not a public-launch decision.</small></p>
   </div>
  </Card>
  <Card title="2. Is Profiles connected to CRM?">
    <p className="studio-readiness-help govuk-body">Run a read-only connection check from CRM to the deployed Profiles Cloudflare Pages service and back to the private CRM Worker. The check also verifies that the Pages database is present and CRM has a Stripe LIVE key. It never creates a payment or reveals any credentials.</p>
    <button className="button secondary govuk-button govuk-button--secondary" type="button" disabled={connectionBusy}
      onClick={()=>void checkConnections()}>{connectionBusy?'Checking connections…':'Verify deployed CRM ↔ Profiles connections'}</button>
    {connectionError&&<p role="alert" className="operation-notice error govuk-body">{connectionError}</p>}
    {!connectionStatus?<p className="studio-readiness-empty govuk-body">Production service connections have not been checked during this session.</p>:
     <div role="status" className="studio-readiness-grid">
      <p className="govuk-body">Overall connection {connectionBadge(connectionStatus.ready)}<small>{explainConnection(connectionStatus.reason)}</small></p>
      <p className="govuk-body">Profiles Pages database {connectionBadge(connectionStatus.pagesDatabaseConfigured)}</p>
      <p className="govuk-body">Pages → CRM private binding {connectionBadge(connectionStatus.privateCrmBindingConfigured)}</p>
      <p className="govuk-body">Round-trip CRM connection {connectionBadge(connectionStatus.crmHandshakeVerified)}</p>
      <p className="govuk-body">CRM Stripe LIVE credentials {connectionBadge(connectionStatus.crmStripeLiveConfigured)}</p>
     </div>}
   </Card>
  <Card title="3. Verify Stripe LIVE plan prices">
   <p className="studio-readiness-help govuk-body">The verification uses Stripe's live API, confirms active GBP prices, billing intervals, expected approved proposal amounts and correct product IDs. It never switches into test mode or displays your API secret.</p>
   {!catalogue?<div className="studio-readiness-empty">Select <strong>Verify all live Stripe prices</strong> to check the connected live catalogue.</div>:
   <>
    <p role="status" className="studio-readiness-summary govuk-body">
     {catalogue.checked?<>Verified {catalogue.livePrices} of {catalogue.count} prices. {catalogue.allValid?'All live catalogue entries passed.':'One or more price checks require attention.'}</>:catalogue.reason||'Verification not available.'}
     {checkedAt&&<small>Checked {new Date(checkedAt).toLocaleString('en-GB')}</small>}
    </p>
    <div className="studio-readiness-table-wrap"><table className="studio-readiness-table govuk-table">
     <thead className="govuk-table__head"><tr className="govuk-table__row"><th className="govuk-table__header" scope="col">Studio plan</th><th className="govuk-table__header" scope="col">Interval</th><th className="govuk-table__header" scope="col">Live amount</th><th className="govuk-table__header" scope="col">Verification</th></tr></thead>
     <tbody className="govuk-table__body">{(catalogue.prices||[]).map(row=><tr className="govuk-table__row" key={row.stripePriceId}>
      <td className="govuk-table__cell"><strong>{row.productCode.replaceAll('_',' ')}</strong><small>{row.stripePriceId}</small></td>
      <td className="govuk-table__cell">{row.interval}</td>
      <td className="govuk-table__cell">{row.unitAmount===null?'—':money(row.unitAmount,'GBP')}</td>
      <td className="govuk-table__cell">{row.valid?<span className="studio-readiness-yes">LIVE · Verified</span>:<span className="studio-readiness-no">{row.reason||'Not verified'}</span>}</td>
     </tr>)}</tbody>
    </table></div>
    {Array.isArray(catalogue.upgradePrices)&&<section className="studio-readiness-upgrade-report">
     <h3 className="govuk-heading-s">Included website → Premium upgrade live-price readiness</h3>
     <p className="studio-readiness-help govuk-body">These are separately governed Stripe LIVE add-ons and must not be confused with the 15 ordinary plans.</p>
     {catalogue.upgradePrices.map(row=><p className="govuk-body" key={row.interval}>
      <strong>{row.interval==='year'?'Annual':'Monthly'} upgrade — £{(row.expected/100).toFixed(2)}: </strong>
      <span className={row.valid?'studio-readiness-yes':'studio-readiness-no'}>{row.valid?'LIVE verified':row.reason}</span>
      <small>{row.id||'No LIVE Price configured'}</small>
     </p>)}
    </section>}
   </>}
  </Card>
  <Card title="4. Record the approved pricing decision">
   <p className="studio-readiness-help govuk-body">Record the genuine Board pricing approval for Profiles and Mini Websites. CRM staff with billing-write permission may save it with an audit record.</p>
   {operations?.settings?.commercialApprovalReference&&<div className="studio-approval-record" role="status">
    <strong>Current recorded decision</strong>
    <p className="govuk-body">Reference: <strong>{operations.settings.commercialApprovalReference}</strong></p>
    <p className="govuk-body">Approved by: {operations.settings.commercialApprovalAuthority||'Not recorded'}</p>
    <p className="govuk-body">Date: {operations.settings.commercialApprovalDate||'Not recorded'}</p>
    <small>CRM updated: {operations.lastUpdatedAt?new Date(operations.lastUpdatedAt).toLocaleString('en-GB'):'Unavailable'}. {config.commercialApprovalRecorded?'Version and reference match.':'Version/reference not yet verified by CRM.'}</small>
   </div>}
   {approvalError&&<p role="alert" className="operation-notice error govuk-body">{approvalError}</p>}
   {approvalNotice&&<p role="status" className="operation-notice govuk-body">{approvalNotice}</p>}
   <form className="studio-approval-form" onSubmit={event=>void recordApproval(event)}>
    <div className="studio-approval-grid">
     <label className="govuk-label">Approved pricing version
      <input className="govuk-input" readOnly value={operations?.effective?.approvedPriceVersion||'Not configured on CRM Worker'}/>
      <small>Matches the version configured on Cloudflare. Current required version: 2026-10-19.</small>
     </label>
     <label className="govuk-label">Actual Board approval reference
      <input className="govuk-input" required type="text" minLength={12} maxLength={240}
       placeholder="Reference from the approval email or signed Board minutes"
       value={approvalForm.reference} onChange={event=>approvalChange('reference',event.target.value)}/>
      <small>Use the genuine decision reference (at least 12 characters).</small>
     </label>
     <label className="govuk-label">Approving person or body
      <input className="govuk-input" required type="text" minLength={3} maxLength={160}
       placeholder="Who authorised the prices?"
       value={approvalForm.authority} onChange={event=>approvalChange('authority',event.target.value)}/>
     </label>
     <label className="govuk-label">Date approval was granted
      <input className="govuk-input" required type="date" max={new Date().toISOString().slice(0,10)}
       value={approvalForm.decidedAt} onChange={event=>approvalChange('decidedAt',event.target.value)}/>
     </label>
    </div>
    <label className="studio-approval-full govuk-label">Governance reason / supporting evidence
     <textarea className="govuk-textarea" required minLength={12} maxLength={1000} rows={3}
      placeholder="Identify the email, resolution or Board minutes and which plan prices were approved."
      value={approvalForm.reason} onChange={event=>approvalChange('reason',event.target.value)}/>
    </label>
    <div className="studio-approval-warning"><AlertTriangle size={19} aria-hidden="true"/>
     <p className="govuk-body"><strong>Before you save:</strong> If commercial and provisioning switches are already ON in Cloudflare, recording this approval may allow real customers to open Stripe LIVE Checkout. You must have verified subscription activation and service access.</p>
    </div>
    <label className="studio-approval-confirm govuk-label">
     <input type="checkbox" checked={approvalForm.acknowledged}
      onChange={event=>approvalChange('acknowledged',event.target.checked)}/>
     <span>I confirm this pricing was approved and LIVE payment-to-service provisioning was verified.</span>
    </label>
    <button type="submit" className="button primary govuk-button" disabled={
     approvalBusy||!settingsDraft||!approvalForm.acknowledged||
     approvalForm.reference.trim().length<12||approvalForm.authority.trim().length<3||
     !approvalForm.decidedAt||approvalForm.reason.trim().length<12||
     operations?.effective?.approvedPriceVersion!=='2026-10-19'
    }>{approvalBusy?'Recording the decision…':'Record approved pricing decision'}</button>
   </form>
  </Card>
  <Card title="5. Manage Studio plans and proposals">
   <p className="studio-readiness-help govuk-body">Govern Studio subscriptions, bundled website upgrades, Multi-Site quantities and proposed promotions here. Only CRM staff with billing-write authority may save these settings. Changes are audited and <strong>never by themselves authorise customer charges or switch Cloudflare billing approval flags.</strong></p>
   {settingsError&&<p role="alert" className="operation-notice error govuk-body">{settingsError}</p>}
   {settingsNotice&&<p role="status" className="operation-notice govuk-body">{settingsNotice}</p>}
   {!settingsDraft?<p className="studio-readiness-empty govuk-body">Studio operational settings are unavailable.</p>:
    <div className="studio-readiness-grid studio-settings-grid">
     <label className="govuk-label">Release stage
      <select className="govuk-select" value={settingsDraft.publicationMode||'launch_requested'} onChange={e=>change('publicationMode',e.target.value)}>
       <option value="internal_testing">Internal technical testing</option>
       <option value="awaiting_release">Awaiting release review</option>
       <option value="live_candidate">Live release candidate (not activated)</option>
       <option value="launch_requested">Public launch requested — activation in progress</option>
       <option value="public_live" disabled={!config.liveCheckoutReady}>Public launch — LIVE (checkouts must be verified)</option>
      </select>
      <small>This audited release stage is not a Stripe payment switch. LIVE status requires separate approval, Cloudflare settings and customer entitlement verification.</small>
     </label>
     <label className="govuk-label">Multi-Site website allowance (CEO authorised: five)
      <input className="govuk-input" type="number" min="0" max="100" step="1" value={settingsDraft.multiSiteRequestedLimit??0}
       onChange={e=>change('multiSiteRequestedLimit',Number(e.target.value))}/>
      <small>Commercial decision: 5 Mini Websites per Multi-Site subscription. CRM must save 5 and the Worker must report an effective approved cap of 5. Currently effective: {operations?.effective?.approvedMultiSiteCapacity||0}. This setting does not authorise payments on its own.</small>
     </label>
     <label className="govuk-label">Bundled Local Business → Premium difference (GBP pence/month)
      <input className="govuk-input" type="number" readOnly value={settingsDraft.includedLocalBusinessToPremiumMonthlyPence??700}/>
      <small>Proposed £7.00 recurring add-on, subject to billing approval and customer ownership validation.</small>
     </label>
     <label className="govuk-label">Stripe LIVE monthly upgrade price reference
      <input className="govuk-input" type="text" autoComplete="off" placeholder="price_…" value={settingsDraft.upgradeMonthlyPriceId||''}
       onChange={e=>change('upgradeMonthlyPriceId',e.target.value)} />
      <small>Staff register a pre-approved LIVE Price ID. This field does not create or activate a Stripe Price.</small>
     </label>
     <label className="govuk-label">Stripe LIVE annual upgrade price reference
      <input className="govuk-input" type="text" autoComplete="off" placeholder="price_…" value={settingsDraft.upgradeAnnualPriceId||''}
       onChange={e=>change('upgradeAnnualPriceId',e.target.value)}/>
     </label>
     <label className="govuk-label">Launch promotion
      <select className="govuk-select" value={settingsDraft.offerLaunchDiscount?'proposed':'disabled'}
       onChange={e=>change('offerLaunchDiscount',e.target.value==='proposed')}>
       <option value="disabled">Not proposed for activation</option>
       <option value="proposed">20% launch campaign proposal (no automatic activation)</option>
      </select>
     </label>
     <label className="govuk-label">Campaign opening
      <input className="govuk-input" type="datetime-local" value={String(settingsDraft.promotionStartsAt||'').slice(0,16)}
       onChange={e=>change('promotionStartsAt',e.target.value)}/>
     </label>
     <label className="govuk-label">Campaign closing
      <input className="govuk-input" type="datetime-local" value={String(settingsDraft.promotionEndsAt||'').slice(0,16)}
       onChange={e=>change('promotionEndsAt',e.target.value)}/>
     </label>
     <p className="studio-settings-full studio-approval-hint govuk-body">Pricing approval is recorded using the audited decision form in section 4 above. Draft plan settings cannot replace a Board approval.</p>
     <label className="studio-settings-full govuk-label">Proposed VAT price presentation (not a tax determination)
      <select className="govuk-select" value={settingsDraft.vatDisplayDraft||'pending'}
       onChange={e=>change('vatDisplayDraft',e.target.value)}>
       <option value="pending">Awaiting tax/accounting confirmation</option>
       <option value="inclusive">Propose VAT-inclusive advertising where applicable</option>
       <option value="exclusive">Propose VAT-exclusive advertising where legally appropriate</option>
      </select>
      <small>Draft only. Verify UK VAT registration/tax treatment and consumer pricing compliance before public launch. This setting never changes Stripe Tax or invoice amounts.</small>
     </label>
     <details className="studio-settings-full studio-settings-advanced" aria-label="Optional advanced plan limits">
      <summary>Advanced settings — 37 optional limits and discount proposals</summary>
      <h3 className="govuk-heading-s">Plan and discount limits — draft only</h3>
      <p className="studio-readiness-help govuk-body">All fields below are proposals only. Leave a field blank when it has not been agreed. Saving a number records it in the CRM audit trail but does not change Stripe pricing, customer rights or launch approval.</p>
      <div className="studio-readiness-grid">
       {boardDraftFields.map(([key,label,max])=><label className="govuk-label" key={key}>{label}
        <input className="govuk-input" type="number" min="0" step="1" max={max}
         value={settingsDraft.boardLimitsDraft?.[key]??''}
         onChange={e=>change('boardLimitsDraft',{
          ...settingsDraft.boardLimitsDraft,
          [key]:e.target.value===''?null:Number(e.target.value),
         })}
         placeholder="Not recorded"/>
       </label>)}
      </div>
     </details>
     <label className="studio-settings-full govuk-label">Governance reason required for every change (12–1000 characters)
      <textarea className="govuk-textarea" minLength="12" maxLength="1000" value={settingsReason}
       onChange={e=>setSettingsReason(e.target.value)} rows="3"
       placeholder="Record why the proposed Studio setting is being changed and the approving authority."/>
     </label>
     <div className="studio-settings-full">
      <button className="button primary govuk-button" type="button" disabled={settingsBusy||settingsReason.trim().length<12}
       onClick={()=>void saveSettings()}>{settingsBusy?'Saving settings…':'Save and audit Studio settings'}</button>
      <button className="button secondary govuk-button govuk-button--secondary" type="button" disabled={settingsBusy}
       onClick={()=>void loadSettings()}>Discard edits / reload</button>
     </div>
    </div>}
  </Card>
  <Card title="Customer Studio subscriptions — live entitlement inspection">
   <p className="studio-readiness-help govuk-body">Read-only view of this customer's Stripe-derived Studio rights. This uses CRM billing authority and does not amend pricing or customer subscriptions.</p>
   <div className="studio-readiness-grid">
    <label className="govuk-label">CRM customer number
     <input className="govuk-input" inputMode="numeric" pattern="[0-9]{10}" maxLength={10}
      value={upgradeTarget.customerNumber}
      onChange={e=>{setUpgradeTarget(v=>({...v,customerNumber:e.target.value.replace(/[^0-9]/g,'')}));setCustomerEntitlements(null)}}
      placeholder="10-digit customer number"/>
    </label>
   </div>
   <button type="button" className="button secondary govuk-button govuk-button--secondary" disabled={entitlementsBusy||upgradeTarget.customerNumber.length!==10}
    onClick={()=>void inspectCustomer()}>{entitlementsBusy?'Reading subscriptions…':'Inspect customer subscriptions'}</button>
   {entitlementsError&&<p role="alert" className="operation-notice error govuk-body">{entitlementsError}</p>}
   {customerEntitlements&&<div role="status" className="studio-readiness-summary">
    <strong>Customer {customerEntitlements.customerNumber}</strong>
    <small>{customerEntitlements.accountSuspended?'Account suspended — no Studio access.':
     (customerEntitlements.subscriptions||[]).length+' active Studio subscription item(s) verified from Stripe LIVE.'}</small>
    {(customerEntitlements.subscriptions||[]).length>0&&<div className="studio-readiness-table-wrap">
     <table className="studio-readiness-table govuk-table">
      <thead className="govuk-table__head"><tr className="govuk-table__row"><th className="govuk-table__header" scope="col">Product</th><th className="govuk-table__header" scope="col">Status</th><th className="govuk-table__header" scope="col">Website allowance</th><th className="govuk-table__header" scope="col">Tier</th></tr></thead>
      <tbody className="govuk-table__body">{customerEntitlements.subscriptions.map((item,index)=><tr className="govuk-table__row" key={(item.subscriptionId||'')+'-'+(item.priceCode||'')+'-'+index}>
       <td className="govuk-table__cell">{String(item.productCode||'Unknown').replaceAll('_',' ')}</td>
       <td className="govuk-table__cell">{item.status||'Unverified'}</td>
       <td className="govuk-table__cell">{Number(item.websiteLimit)||0}</td>
       <td className="govuk-table__cell">{item.websiteTier||'—'}</td>
      </tr>)}</tbody>
     </table>
    </div>}
   </div>}
  </Card>
  <Card title="Included website upgrade — eligibility and recurring price quote">
   <p className="studio-readiness-help govuk-body">Staff can check an actual customer's entitlement and owned website before offering a Premium upgrade. This is a <strong>read-only quote</strong>; it cannot create Stripe sessions or charge a customer. Customer payment must be confirmed through the authenticated My Sousa Murray checkout and configured CRM service bindings.</p>
   <div className="studio-readiness-grid">
    <label className="govuk-label">CRM customer number
     <input className="govuk-input" inputMode="numeric" pattern="[0-9]{10}" maxLength={10} value={upgradeTarget.customerNumber}
      onChange={e=>setUpgradeTarget(v=>({...v,customerNumber:e.target.value.replace(/[^0-9]/g,'')}))} placeholder="10-digit customer number"/>
    </label>
    <label className="govuk-label">Website ID
     <input className="govuk-input" type="number" min="1" step="1" value={upgradeTarget.site_id}
      onChange={e=>setUpgradeTarget(v=>({...v,site_id:e.target.value}))}/>
    </label>
    <label className="govuk-label">Website type
     <select className="govuk-select" value={upgradeTarget.scope} onChange={e=>setUpgradeTarget(v=>({...v,scope:e.target.value}))}>
      <option value="standalone">Standalone Mini Website</option>
      <option value="profile">Existing Profile-linked Mini Website</option>
     </select>
    </label>
    <label className="govuk-label">Billing cycle
     <select className="govuk-select" value={upgradeTarget.interval} onChange={e=>setUpgradeTarget(v=>({...v,interval:e.target.value}))}>
      <option value="month">Monthly</option>
      <option value="year">Annual</option>
     </select>
    </label>
   </div>
   <button type="button" className="button secondary govuk-button govuk-button--secondary" disabled={upgradeQuoteBusy||upgradeTarget.customerNumber.length!==10||!Number.isSafeInteger(Number(upgradeTarget.site_id))||Number(upgradeTarget.site_id)<1}
    onClick={()=>void verifyUpgrade()}>{upgradeQuoteBusy?'Checking eligibility…':'Verify owned website and quote'}</button>
   {upgradeQuoteError&&<p role="alert" className="operation-notice error govuk-body">{upgradeQuoteError}</p>}
   {upgradeQuote&&<p role="status" className="studio-readiness-summary govuk-body">
    <strong>Professional included Local Business → Premium:</strong> £{(upgradeQuote.amountPence/100).toFixed(2)}/{upgradeQuote.interval==='year'?'year':'month'}.
    Stripe LIVE Checkout: <strong className={upgradeQuote.checkoutReady?'studio-readiness-yes':'studio-readiness-no'}>
     {upgradeQuote.checkoutReady?'Available after customer consent':upgradeQuote.reason||'Not ready'}
    </strong>
    <small>Verified site {upgradeQuote.siteId} for this CRM customer. Quotes do not grant access or create a charge.</small>
   </p>}
  </Card>
  <Card title="Payment lifecycle and release safeguards">
   <p className="studio-readiness-help govuk-body">Customer payment journeys must be tested end to end: SM ID sign-in → CRM customer link → hosted Stripe LIVE Checkout → verified subscription entitlement → website publishing → cancellation and expiry. A live price check alone does not prove these journeys work.</p>
   <div className="studio-readiness-warning"><ShieldCheck size={18}/>
     <p className="govuk-body">Only CRM can authorise paid checkout, invoice charging and entitlement assignment. The historic Profiles Admin Centre remains unchanged until the final migration phase.</p>
   </div>
   <p className="studio-readiness-help govuk-body"><AlertTriangle size={15} aria-hidden="true"/> Changing pricing, promotions, live charges or public launch requires the separately recorded authorisation and operational controls.</p>
   <a className="studio-readiness-link" href="https://dashboard.stripe.com/prices" target="_blank" rel="noopener noreferrer">
    Open Stripe live catalogue <ExternalLink size={15}/>
   </a>
  </Card>
 </main>;
}
